/**
 * iter 71.2 — Locations page now uses the canonical LocationManagerDrawer
 * (the same drawer the Settings → My Organization page mounts). The old
 * inline drawer (no Google Places, no contact / phone / lat-lng, no dirty
 * guard) is gone — this page just lists rows + delegates the form to the
 * unified component with locationKind="service" + types / priorities props.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Page } from "../components/AppShell";
import { Plus, MapPin } from "@phosphor-icons/react";
import useBulkSelect from "../lib/useBulkSelect";
import LocationManagerDrawer from "../components/OrgAdmin/LocationManagerDrawer";

export default function Locations() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  // Drawer state: open=bool, editing=null|record. null = create mode.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/locations").then(r => setItems(r.data));
    api.get("/locations/types").then(r => setTypes(r.data));
    api.get("/locations/priorities").then(r => setPriorities(r.data));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (l) => { setEditing(l); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setEditing(null); };

  const bulk = useBulkSelect(items, { testid: "location-row" });

  return (
    <Page
      title="Locations"
      subtitle="ADDRESSES / COURTS / SERVICE SITES"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Locations"]}
      actions={
        <button
          className="btn btn-primary"
          onClick={openCreate}
          data-testid="new-location-btn"
        >
          <Plus size={14} /> NEW LOCATION
        </button>
      }
      testid="locations-page"
    >
      <div className="surface">
        {items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="empty-locations">
            <MapPin size={32} weight="thin" className="mx-auto mb-3" />
            No locations yet.
          </div>
        ) : (
          <table className="dense">
            <thead><tr>
              <th className="w-8"><input {...bulk.allProps()} /></th>
              <th>NAME</th><th>ADDRESS</th><th>CITY/STATE</th><th>TYPE</th><th>PRIORITY</th>
            </tr></thead>
            <tbody>
              {items.map(l => (
                <tr key={l.id} data-testid={`location-row-${l.id}`}>
                  <td><input {...bulk.rowProps(l.id)} /></td>
                  <td>
                    <button
                      className="link-cell font-semibold"
                      onClick={() => openEdit(l)}
                      data-testid={`location-row-open-${l.id}`}
                      title="Open detailed view"
                    >
                      {l.name}
                    </button>
                  </td>
                  <td>{l.address1 || "—"}{l.address2 ? `, ${l.address2}` : ""}</td>
                  <td>{[l.city, l.state, l.postal_code].filter(Boolean).join(", ") || "—"}</td>
                  <td>{types.find(t => t.id === l.location_type_id)?.name || "—"}</td>
                  <td>{priorities.find(p => p.id === l.location_priority_id)?.name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <LocationManagerDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSuccess={load}
        mode={editing ? "edit" : "create"}
        initialData={editing}
        locationKind="service"
        types={types}
        priorities={priorities}
        /* orgId is unused for service-kind (drawer routes to /api/locations). */
      />
    </Page>
  );
}
